/**
 * /tenant-plane/$spokeId — Workspace Spoke runtime host. Surface Layer (/01)
 *
 * Spoke-scoped runtime container. Renders the workspace chrome for one Spoke
 * (resolved from the :spokeId route param) and reserves the mount point where
 * the spoke-owned surface attaches. Outside-in: the host is correct even while
 * the spoke read models are pending — it never fetches or initializes a Spoke
 * SDK directly; the spoke surface mounts here once its contracts land.
 */

import type { ComponentType } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Column, Grid, SkeletonText, Tag, Tile } from '@carbon/react';
import { Application } from '@carbon/icons-react';
import { usePluginContracts } from '@lib/persistence/useSurfaceContracts';
import { KnowledgeIntelligence } from '../surfaces/tenant-plane/spokes/KnowledgeIntelligence';
import '../styles/views.scss';

export const Route = createFileRoute('/tenant-plane/$spokeId')({
  component: SpokeHostView,
});

/**
 * Built-surface map: a spoke whose id has a BUILT surface module here mounts it; any other spoke
 * (no entry) falls through to the generic outside-in placeholder. The mount gate is presence in this
 * map — NOT the registry `status` field (the registry `active` flag is the catalog marker; this host
 * does not read it). REQ-001.91 F6 ships the first entry (spoke_knowledge_intelligence — a read-only
 * tenant projection), and its registry status is correspondingly `active`. The host itself never reads
 * spoke data or dispatches a write; the mounted surface owns its own (read-only) reads.
 */
const SPOKE_SURFACES: Record<string, ComponentType> = {
  spoke_knowledge_intelligence: KnowledgeIntelligence,
};

function SpokeHostView() {
  const { spokeId } = Route.useParams();
  const { rows, isLoading } = usePluginContracts();
  const spokePlugins = rows?.filter((row) => row.spoke_key === spokeId) ?? [];
  const SpokeSurface = SPOKE_SURFACES[spokeId];

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Workspace · Spoke</h1>
        <p className="view__subtitle">
          Spoke runtime host for <strong>{spokeId}</strong>. Workspace chrome is shell-owned; the spoke surface mounts inside this scope.
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <div className="view-tile__row">
                <h2 className="view-tile__value">Spoke runtime</h2>
                <Tag type={SpokeSurface ? 'green' : 'cool-gray'}>{spokeId}</Tag>
              </div>
              <p className="view-tile__label">
                This container is scoped to a single Spoke. It hosts the spoke-owned surface tree and routes ActionFrames through the same command contract as native engines. The host does not load a Spoke SDK or read spoke data directly.
              </p>
            </Tile>
          </Column>
        </Grid>
      </div>

      {SpokeSurface ? (
        <SpokeSurface />
      ) : (
        <div className="surface-grid">
          <Grid condensed>
            {isLoading && !rows ? (
              <Column sm={4} md={8} lg={16}>
                <SkeletonText paragraph lineCount={3} />
              </Column>
            ) : spokePlugins.length > 0 ? (
              spokePlugins.map((plugin) => (
                <Column key={plugin.plugin_key} sm={4} md={4} lg={4}>
                  <Tile className="view-tile">
                    <div className="view-tile__row">
                      <strong>{plugin.plugin_key}</strong>
                      <Tag type={plugin.status === 'enabled' ? 'green' : 'cool-gray'}>{plugin.status}</Tag>
                    </div>
                    <p className="view-tile__label">Plugin bound to this spoke via the catalog.</p>
                  </Tile>
                </Column>
              ))
            ) : (
              <Column sm={4} md={8} lg={16}>
                <Tile className="view-empty">
                  <Application size={32} aria-hidden="true" />
                  <h2>Spoke surface mounts here</h2>
                  <p className="view-tile__label">
                    The spoke-owned read models are pending. Once the Spoke publishes its surface contracts, its surface renders in this scope without changing the workspace shell.
                  </p>
                  <Tag type="cool-gray">Outside-in · pending</Tag>
                </Tile>
              </Column>
            )}
          </Grid>
        </div>
      )}
    </div>
  );
}
